// The one place a date becomes something a reader sees.
//
// mud/10-data-formatting.md Part 2 calls itself a "binding formatting contract",
// and it prints `07.07.2024` and `7:46:35 AM` as the anti-patterns — in red. Every
// formatter here used to produce exactly the first of those (`toLocaleDateString`
// with 2-digit day and month), which is how the whole product came to be written
// in the format MUD names as the thing to stop doing.
//
// The contract, for reference, because these functions are only readable next to
// it:
//
//   today / yesterday          Astăzi · Ieri            (a date, not a duration)
//   inside the current year    15 septembrie            (the year is implied)
//   any other year             15 septembrie 2027
//   where space is short       15 sept. 2027            (tables, lists, cells)
//
//   Romanian and Russian put the day before the month and use a 24-hour clock;
//   English writes `September 15, 2027` and uses AM/PM. Seconds are never shown.
//
// `formatDate` is the short form because its callers are registers and tables —
// thirty of them — where the column is the constraint. `formatDateLong` is the
// same date spelled out, for a detail page that has the room.
//
// The month names live here rather than in the i18n catalogues for the reason
// LOCALE_LABELS gives in libs/ui/i18n/src/lib/locales.ts: they are proper nouns
// in each language's own script, not copy anybody translates. The wireframe
// declares them the same way (wireframes/react/src/lib/session.ts).

export type DateLocale = 'ro' | 'ru' | 'en';

const MONTHS_SHORT: Record<DateLocale, readonly string[]> = {
  ro: [
    'ian.',
    'feb.',
    'mar.',
    'apr.',
    'mai',
    'iun.',
    'iul.',
    'aug.',
    'sept.',
    'oct.',
    'nov.',
    'dec.',
  ],
  ru: [
    'янв.',
    'февр.',
    'мар.',
    'апр.',
    'мая',
    'июн.',
    'июл.',
    'авг.',
    'сент.',
    'окт.',
    'нояб.',
    'дек.',
  ],
  en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
};

const MONTHS_LONG: Record<DateLocale, readonly string[]> = {
  ro: [
    'ianuarie',
    'februarie',
    'martie',
    'aprilie',
    'mai',
    'iunie',
    'iulie',
    'august',
    'septembrie',
    'octombrie',
    'noiembrie',
    'decembrie',
  ],
  // Genitive, because these only ever appear after a day number ("15 сентября").
  ru: [
    'января',
    'февраля',
    'марта',
    'апреля',
    'мая',
    'июня',
    'июля',
    'августа',
    'сентября',
    'октября',
    'ноября',
    'декабря',
  ],
  en: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
};

const TODAY: Record<DateLocale, string> = { ro: 'Astăzi', ru: 'Сегодня', en: 'Today' };
const YESTERDAY: Record<DateLocale, string> = { ro: 'Ieri', ru: 'Вчера', en: 'Yesterday' };

// One module-level setting, the same shape as dayjs's locale and correct only
// while it matches what the interface is speaking. `bindLanguageSideEffects`
// (apps/frontoffice/src/app/language-side-effects.ts) is what keeps it true —
// this library carries no translation runtime and deliberately does not import
// one, so it cannot read the language for itself.
let activeLocale: DateLocale = 'ro';

export function setDateLocale(locale: string | undefined): void {
  if (locale === 'ro' || locale === 'ru' || locale === 'en') activeLocale = locale;
}

export function getDateLocale(): DateLocale {
  return activeLocale;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const ISO_MONTH = /^\d{4}-\d{2}$/;
const DISPLAY_MONTH = /^(\d{2})\/(\d{4})$/;

interface CalendarDate {
  year: number;
  month: number; // 1-12
  day: number;
  /** Absent for a date-only value: a calendar date has no instant behind it. */
  time?: { hour: number; minute: number };
}

/**
 * A wire value as a calendar date, or `null` when it is not one.
 *
 * A bare `YYYY-MM-DD` is read off the string and never through `Date`:
 * `new Date('2027-03-17')` is UTC midnight, which renders as the 16th anywhere
 * west of Greenwich. Anything longer is an instant, and an instant is shown in
 * the reader's own zone, which is what `Date` is for.
 */
function parse(value: string): CalendarDate | null {
  if (ISO_DATE.test(value)) {
    return {
      year: Number(value.slice(0, 4)),
      month: Number(value.slice(5, 7)),
      day: Number(value.slice(8, 10)),
    };
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
    time: { hour: date.getHours(), minute: date.getMinutes() },
  };
}

/** `{ year, month, day }` for the reader's own today. */
function todayParts(): { year: number; month: number; day: number } {
  const now = new Date();
  return { year: now.getFullYear(), month: now.getMonth() + 1, day: now.getDate() };
}

/** How many days `d` is before today, for the two values the contract names. */
function daysBeforeToday(d: CalendarDate): number {
  const then = Date.UTC(d.year, d.month - 1, d.day);
  const today = todayParts();
  const now = Date.UTC(today.year, today.month - 1, today.day);
  return Math.round((now - then) / 86_400_000);
}

/**
 * Day, month and year in the order the language writes them. English puts the
 * month first and separates the year with a comma; Romanian and Russian do not.
 */
function composeDate(d: CalendarDate, long: boolean, withYear: boolean): string {
  const locale = activeLocale;
  const month = (long ? MONTHS_LONG : MONTHS_SHORT)[locale][d.month - 1];
  if (locale === 'en') {
    return withYear ? `${month} ${d.day}, ${d.year}` : `${month} ${d.day}`;
  }
  return withYear ? `${d.day} ${month} ${d.year}` : `${d.day} ${month}`;
}

/**
 * The clock. English is 12-hour with AM/PM, Romanian and Russian are 24-hour,
 * and none of the three shows seconds — `7:46:35 AM` is the contract's own
 * example of what not to print. The hour is not zero-padded: MUD writes `7:46`.
 */
function composeTime(time: { hour: number; minute: number }): string {
  const minute = String(time.minute).padStart(2, '0');
  if (activeLocale !== 'en') return `${time.hour}:${minute}`;
  const suffix = time.hour < 12 ? 'AM' : 'PM';
  const hour = time.hour % 12 === 0 ? 12 : time.hour % 12;
  return `${hour}:${minute} ${suffix}`;
}

function formatCalendarDate(d: CalendarDate, long: boolean): string {
  const near = daysBeforeToday(d);
  if (near === 0) return TODAY[activeLocale];
  if (near === 1) return YESTERDAY[activeLocale];
  return composeDate(d, long, d.year !== todayParts().year);
}

/**
 * A date as a register or a table cell should carry it — the abbreviated month,
 * because that is the form the contract gives for limited space, and every
 * caller of this function is a column.
 */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '';
  if (ISO_MONTH.test(iso)) return formatMonth(iso, false);
  const parsed = parse(iso);
  if (!parsed) return iso;
  return formatCalendarDate(parsed, false);
}

/** The same date spelled out, for a detail page with the room for it. */
export function formatDateLong(iso: string | null | undefined): string {
  if (!iso) return '';
  if (ISO_MONTH.test(iso)) return formatMonth(iso, true);
  const parsed = parse(iso);
  if (!parsed) return iso;
  return formatCalendarDate(parsed, true);
}

/** A month with no day: `sept. 2027`. */
function formatMonth(iso: string, long: boolean): string {
  const month = Number(iso.slice(5, 7));
  if (month < 1 || month > 12) return iso;
  return `${(long ? MONTHS_LONG : MONTHS_SHORT)[activeLocale][month - 1]} ${iso.slice(0, 4)}`;
}

/**
 * An instant: the date, a comma, and the clock — `Astăzi, 7:46`, the contract's
 * own example. A value that carries no time of day falls back to the date,
 * because inventing `00:00` would state a precision the value does not have.
 */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const parsed = parse(iso);
  if (!parsed) return iso;
  const date = formatCalendarDate(parsed, false);
  return parsed.time ? `${date}, ${composeTime(parsed.time)}` : date;
}

// ─── Entry ───────────────────────────────────────────────────────────────────
//
// Reading a date and typing one are different problems, and this is the second.
// `mud-date-input` accepts the digits its `format` permits and rewrites the
// separator inline, so the format is a mask rather than a rendering: what it
// governs is the order the operator types in.
//
// That order is day-month-year here. It is what AGE itself defaults to
// (`format: 'DD/MM/YYYY'`), what the wireframe writes on the field
// (`ZZ/LL/AAAA`), and the order Moldova reads a date in. Half the date fields in
// this product were pinned to `YYYY-MM-DD` instead — not as a decision about
// entry, but because with an ISO mask the displayed value and the wire value are
// the same string and no conversion has to be written. These two functions are
// that conversion, written once, so no field has to choose the wrong order to
// avoid it.

const DISPLAY_DATE = /^(\d{2})\/(\d{2})\/(\d{4})$/;

/** A wire `YYYY-MM-DD` as the field shows it: `2027-03-17` → `17/03/2027`. */
export function isoToDateInput(iso: string | null | undefined): string {
  if (!iso || !ISO_DATE.test(iso)) return '';
  return `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
}

/**
 * What stands in the field, back to a wire value. Empty while the entry is not
 * yet a whole date — a half-typed `17/03` names no day, and every caller reads
 * that emptiness as "the filter is not set" or "the field is not filled in".
 */
export function dateInputToIso(display: string): string {
  const match = DISPLAY_DATE.exec(display.trim());
  if (!match) return '';
  const [, day, month, year] = match as unknown as [string, string, string, string];
  return `${year}-${month}-${day}`;
}

/** Just the clock, for a cell whose row already says which day it is. */
export function formatTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const parsed = parse(iso);
  if (!parsed?.time) return '';
  return composeTime(parsed.time);
}

// An expiry is entered as a month and a year (owner ruling, 6 September 2026;
// caiet UC50-02 and UC40-05 give the rule its meaning: a lot reported without a
// day expires at the end of the month). The stored value stays a full
// YYYY-MM-DD, so the completion has to happen somewhere, and this module is the
// one place it happens for anything a human enters. See
// specs/041-month-expiry/spec.md for why the browser and not the service.

// (ISO_DATE, ISO_MONTH and DISPLAY_MONTH are declared with the display
// formatters above — one set of shapes for the whole module.)

/** `YYYY-MM` (or a full date) to the last day of that month, as `YYYY-MM-DD`. */
export function lastDayOfMonth(value: string): string {
  const month = value.slice(0, 7);
  if (!ISO_MONTH.test(month)) return value;
  const [year, monthNumber] = month.split('-').map(Number) as [number, number];
  if (monthNumber < 1 || monthNumber > 12) return value;
  // Day 0 of the next month is the last day of this one, and Date.UTC does the
  // leap year without a table.
  const day = new Date(Date.UTC(year, monthNumber, 0)).getUTCDate();
  return `${month}-${String(day).padStart(2, '0')}`;
}

/**
 * Whether an ISO date falls on the last day of its own month. A fact about the
 * calendar only. It does not tell how a date was reported, and no display may
 * use it for that: `formatExpiry` takes the stored precision instead.
 */
export function isEndOfMonth(iso: string): boolean {
  return ISO_DATE.test(iso) && lastDayOfMonth(iso) === iso;
}

/** Digits typed into the month field, with the separator inserted: `LL/AAAA`. */
export function maskMonthInput(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 6);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}/${digits.slice(2)}`;
}

/**
 * `LL/AAAA` as the operator typed it, to the wire value. Empty string when the
 * field is not yet a whole month — an incomplete entry has no date to stand for,
 * and the screens read that emptiness as "not filled in".
 */
export function monthInputToWireDate(display: string): string {
  const match = DISPLAY_MONTH.exec(display.trim());
  if (!match) return '';
  const [, month, year] = match as unknown as [string, string, string];
  if (Number(month) < 1 || Number(month) > 12) return '';
  return lastDayOfMonth(`${year}-${month}`);
}

/**
 * `LL/AAAA` as the operator typed it, to the month as the wire carries it:
 * `YYYY-MM`. Core stores that with the `MONTH` precision, so the lot is shown as
 * a month on every later screen (docs/shape-ops-lot-expiry-precision.md D6); the
 * month's last day, which `monthInputToWireDate` returns, is stored as a `DAY`.
 * Empty string while the field is not yet a whole month.
 */
export function monthInputToWireMonth(display: string): string {
  const wire = monthInputToWireDate(display);
  return wire === '' ? '' : wire.slice(0, 7);
}

/** The wire value back into the month field: `2027-03-17` shows as `03/2027`. */
export function wireDateToMonthInput(iso: string): string {
  if (!ISO_DATE.test(iso) && !ISO_MONTH.test(iso)) return '';
  return `${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
}

/**
 * How an expiry was reported, as the server stores it with the lot
 * (`core.lot.expiry_precision`, docs/shape-ops-lot-expiry-precision.md D2).
 */
export type ExpiryPrecision = 'DAY' | 'MONTH';

/**
 * An expiry as a reader should see it, by the precision it was reported with.
 * `MONTH` is shown as the month (`03/2027`), because that is what the source
 * gave and the lot is available to the month's end. Anything else is shown as
 * the full date, including a day that happens to be the last of its month:
 * the owner's rule (2026-10-13) is that the stored precision decides the
 * display, and the date value never does. `29.02.2028` stays a day when it was
 * reported as one.
 *
 * A bare `YYYY-MM` carries its own precision in its shape and is always a month.
 * A full date with no precision is shown in full: that is the only reading
 * which claims nothing the source did not say.
 *
 * `03/2027` is the one numeric date form left in the product, and it is here on
 * purpose rather than by omission: it mirrors the `LL/AAAA` mask the operator
 * types into (spec 041), so the field and the cell read the same. Changing it to
 * `sept. 2027` is an owner's call, not this module's.
 */
export function formatExpiry(
  value: string | null | undefined,
  precision?: ExpiryPrecision | string | null,
): string {
  if (!value) return '';
  if (ISO_MONTH.test(value)) return wireDateToMonthInput(value);
  if (!ISO_DATE.test(value)) return formatDate(value);
  if (precision === 'MONTH') return wireDateToMonthInput(value);
  // A calendar date with no instant behind it, so it is shown the way every other
  // date in the product is. `formatDate` reads a bare `YYYY-MM-DD` off the string
  // rather than through `Date`: `new Date('2027-03-17')` is UTC midnight, the
  // 16th west of Greenwich.
  return formatDate(value);
}
